/**
 * Bottom Navigation Component for Lobby2.0
 * Horizontal scrollable navigation with tabs for each section
 */

import React, { memo } from 'react';
import { LOBBY_SECTION_NAME } from '@repo/common';
import { useOpenType } from '../../../../core/providers/OpenTypeContext';
import { useNavArrows } from '../../hooks/useNavArrows';
import {
  NavigationWrapper,
  NavigationContainer,
  LobbyTabContainer,
  ScrollableTabsContainer,
  TabsContainer,
} from './NavigationStyles';
import NavArrows from './NavArrows';
import NavigationTab from './NavigationTab';
import { useNavigation } from '../../hooks/useNavigation';
import { FX_ENABLED, NavSweep } from 'shared/effects';

interface NavigationProps {
  className?: string;
  style?: React.CSSProperties;
  hidden?: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ className, style, hidden }) => {
  const { openType } = useOpenType();

  const {
    tabsContainerRef,
    tabRefs,
    scrollableTabs,
    selectedSection,
    viewMode,
    isTransitioning,
    createTrackedTabClick,
  } = useNavigation();

  const { navScrollArrows, handleNavMouseEnter, handleNavMouseLeave, scrollNavLeft, scrollNavRight } =
    useNavArrows(tabsContainerRef);

  const lobbyTab = {
    name: LOBBY_SECTION_NAME,
    games: [],
    icon: 'lobby_icon',
    jackpotName: null,
    banner: false,
    gridBlocks: 0,
  };

  return (
    <NavigationWrapper className={`egt_navigation_container ${className || ''}`} $openType={openType} $hidden={hidden} style={style}>
      <NavigationContainer $openType={openType} onMouseEnter={handleNavMouseEnter} onMouseLeave={handleNavMouseLeave}>
        {/* POC animated lobby (?fx=1): periodic light sweep, one band element, nothing with the flag off. */}
        {FX_ENABLED && <NavSweep />}
        <LobbyTabContainer>
          <NavigationTab
            sectionName={lobbyTab.name}
            isActive={viewMode === 'multi'}
            isDisabled={isTransitioning}
            onClick={createTrackedTabClick(lobbyTab.name)}
            tabRefs={tabRefs}
            isLobbyTab={true}
          />
        </LobbyTabContainer>
        <ScrollableTabsContainer>
          <TabsContainer $openType={openType} $show={false} ref={tabsContainerRef}>
            {scrollableTabs.map((section) => {
              // Lobby tab is active in multi-view, others are active in single-view
              const isActive =
                section.name === LOBBY_SECTION_NAME
                  ? viewMode === 'multi'
                  : selectedSection === section.name && viewMode === 'single';

              return (
                <NavigationTab
                  key={section.name}
                  sectionName={section.name}
                  isActive={isActive}
                  isDisabled={isTransitioning}
                  onClick={createTrackedTabClick(section.name)}
                  tabRefs={tabRefs}
                />
              );
            })}
          </TabsContainer>
          {/* Navigation Scroll Arrows - Desktop only */}
          <NavArrows
            navScrollArrows={navScrollArrows}
            showTournamentCup={false}
            scrollNavLeft={scrollNavLeft}
            scrollNavRight={scrollNavRight}
          />
        </ScrollableTabsContainer>
      </NavigationContainer>
    </NavigationWrapper>
  );
};

// PERFORMANCE OPTIMIZED: Memoized to prevent re-renders when props haven't changed
export default memo(Navigation, (prevProps, nextProps) => {
  return prevProps.className === nextProps.className && prevProps.hidden === nextProps.hidden;
});
